<template>
    <div class="signoff-sales-tab">
        <div v-if="loading && !rows.length" class="text-muted">Loading…</div>
        <div v-else-if="!rows.length" class="text-muted">
            <i class="fa fa-check-circle"></i>
            No drafts awaiting sales signoff. You're all caught up.
        </div>
        <table v-else class="table table-hover">
            <thead>
                <tr>
                    <th style="width: 40px;">&nbsp;</th>
                    <th>Quote</th>
                    <th>Salesperson</th>
                    <th>Submitted</th>
                    <th style="width: 80px; text-align: center;">Approve</th>
                </tr>
            </thead>
            <tbody>
                <template v-for="row in rows">
                    <tr :key="row.id" @click="toggleExpand(row.id)" style="cursor: pointer;">
                        <td>#{{ row.quote.id }}</td>
                        <td>
                            <strong>{{ row.quote.name }}</strong>
                            <span v-if="row.quote.company && row.quote.company !== row.quote.name" class="text-muted">
                                — {{ row.quote.company }}
                            </span>
                        </td>
                        <td>
                            <small>{{ row.quote.salesperson ? row.quote.salesperson.name : '—' }}</small>
                        </td>
                        <td>
                            <small>{{ formatRelative(row.submitted_at) }}</small>
                            <span v-if="row.submitter" class="text-muted"> by {{ row.submitter.name }}</span>
                        </td>
                        <td style="text-align: center;" @click.stop>
                            <input
                                type="checkbox"
                                :disabled="approving[row.id]"
                                @change="onApprove(row)"
                                title="Tick to approve"
                            />
                            <button
                                v-if="canOverride"
                                type="button"
                                class="btn btn-default btn-xs"
                                style="margin-left: 6px;"
                                :disabled="approving[row.id]"
                                @click.stop="onOverride(row)"
                                title="Override — sign off on behalf of the queued approver with a reason"
                            ><i class="fa fa-shield"></i> Override</button>
                        </td>
                    </tr>
                    <tr v-if="expanded === row.id" :key="row.id + '-detail'">
                        <td colspan="5" style="background: #f8f9fa; padding: 12px 24px;">
                            <div v-if="loadingDetail">Loading detail…</div>
                            <div v-else-if="detail && detail.id === row.quote.id">
                                <div v-if="detail.modified_since" class="alert alert-warning" style="padding: 6px 10px;">
                                    <small><i class="fa fa-exclamation-triangle"></i> This quote has been modified since the original submit. Re-check carefully.</small>
                                </div>

                                <!-- Customer & contact details -->
                                <div class="row" style="margin: 0 0 8px;" v-if="detail.customer">
                                    <div class="col-sm-6" style="padding-left: 0;">
                                        <div v-if="detail.company"><strong>Company:</strong> {{ detail.company }}</div>
                                        <div v-if="detail.customer.email"><strong>Email:</strong> {{ detail.customer.email }}</div>
                                        <div v-if="detail.customer.phone"><strong>Phone:</strong> {{ detail.customer.phone }}</div>
                                        <div v-if="detail.customer.postcode"><strong>Postcode:</strong> {{ detail.customer.postcode }}</div>
                                    </div>
                                    <div class="col-sm-6">
                                        <div v-if="detail.customer.segment"><strong>Segment:</strong> {{ detail.customer.segment }}</div>
                                        <div v-if="detail.customer.client_status"><strong>Customer:</strong> {{ detail.customer.client_status }}</div>
                                        <div v-if="detail.salesperson"><strong>Salesperson:</strong> {{ detail.salesperson }}</div>
                                    </div>
                                </div>

                                <div><strong>Total ex VAT:</strong> £{{ detail.total_pounds.toFixed(2) }}</div>
                                <div v-if="detail.deadline && detail.deadline.text">
                                    <strong>Deadline:</strong>
                                    <template v-if="detail.deadline.parsed">
                                        <span :class="{ 'text-danger': detail.deadline.overdue }">{{ detail.deadline.date }}</span>
                                        <span class="text-muted">(from “{{ detail.deadline.text }}”)</span>
                                        <span v-if="detail.deadline.overdue" class="label label-danger" style="font-size: 0.7em;">overdue</span>
                                    </template>
                                    <template v-else>
                                        <span>“{{ detail.deadline.text }}”</span>
                                        <span class="text-warning"><i class="fa fa-exclamation-triangle"></i> couldn’t read as a date — check manually</span>
                                    </template>
                                </div>
                                <h5 style="margin-top: 12px;">Items</h5>
                                <ul>
                                    <li v-for="item in detail.items" :key="item.id" style="margin-bottom: 8px;">
                                        <strong>{{ item.name }}</strong>
                                        (qty {{ item.quantity }}, £{{ (item.price / 100).toFixed(2) }})
                                        <span v-if="item.from_template_id" class="label label-success" style="font-size: 0.7em;">from template</span>
                                        <span v-else class="label label-warning" style="font-size: 0.7em;">bespoke</span>
                                        <div v-if="itemSize(item)" class="text-muted">
                                            <small>Size: {{ itemSize(item) }}</small>
                                        </div>
                                        <div v-if="item.spec && item.spec.length" class="text-muted">
                                            <small>
                                                <span v-for="(m, idx) in item.spec" :key="idx">
                                                    <span v-if="idx"> · </span>{{ m.label }}: {{ m.value }}</span>
                                            </small>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
    </div>
</template>

<script>
/*
    Sales tab — Tim's queue of submitted drafts. List + inline detail
    panel + tick-to-approve, all in one component since the Phase 2
    scope here is just Sales. Phase 3 adds SignoffWonTab, Phase 4
    adds SignoffInspectionTab.

    Approving via the checkbox POSTs to the approve endpoint and
    removes the row from the list on success. The parent
    (SignoffPage) is notified via 'approved' so it can refresh the
    pending-counts badge immediately.

    See planning/quote-signoff-design.md §7.7 and §10.5.
*/
export default {
    props: {
        canOverride: { type: Boolean, default: false },
    },
    data() {
        return {
            rows: [],
            loading: true,
            expanded: null,
            detail: null,
            loadingDetail: false,
            approving: {},
        };
    },
    mounted() {
        this.refresh();
    },
    methods: {
        refresh() {
            this.loading = true;
            fetch('/api/signoff/sales/list', { headers: { Accept: 'application/json' }})
                .then(r => r.json())
                .then(data => {
                    this.rows = data.data || data;   // paginated wrapper or plain array
                    this.loading = false;
                })
                .catch(() => { this.loading = false; });
        },
        toggleExpand(rowId) {
            if (this.expanded === rowId) {
                this.expanded = null;
                this.detail = null;
                return;
            }
            this.expanded = rowId;
            const row = this.rows.find(r => r.id === rowId);
            if (!row) return;
            this.loadingDetail = true;
            this.detail = null;
            fetch(`/api/signoff/${row.quote.id}/sales-detail`, { headers: { Accept: 'application/json' }})
                .then(r => r.json())
                .then(data => {
                    this.detail = data;
                    this.loadingDetail = false;
                })
                .catch(() => { this.loadingDetail = false; });
        },
        onApprove(row) {
            if (this.approving[row.id]) return;
            this.$set(this.approving, row.id, true);

            const token = document.querySelector('meta[name="csrf-token"]')?.content || '';
            fetch(`/quote/${row.quote.id}/signoff/sales/approve`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': token,
                },
            })
                .then(r => r.json().then(body => ({ status: r.status, body })))
                .then(({ status, body }) => {
                    if (status >= 400) {
                        alert(body.error || 'Approve failed');
                        this.$set(this.approving, row.id, false);
                        return;
                    }
                    // Remove the row from the local list and notify parent
                    this.rows = this.rows.filter(r => r.id !== row.id);
                    if (this.expanded === row.id) this.expanded = null;
                    this.$emit('approved', { quoteId: row.quote.id, stage: 'sales' });
                })
                .catch(err => {
                    alert('Approve failed: ' + (err.message || 'network error'));
                    this.$set(this.approving, row.id, false);
                });
        },
        onOverride(row) {
            if (this.approving[row.id]) return;
            const reason = window.prompt(
                'OVERRIDE sales signoff for #' + row.quote.id + ' — sign off on behalf of the queued approver.\n\nReason (min 10 chars):',
                ''
            );
            if (reason === null || reason.trim().length < 10) {
                if (reason !== null) alert('Override cancelled — reason must be at least 10 characters.');
                return;
            }
            this.$set(this.approving, row.id, true);

            const token = document.querySelector('meta[name="csrf-token"]')?.content || '';
            fetch(`/quote/${row.quote.id}/signoff/sales/override`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': token,
                },
                body: JSON.stringify({ override_reason: reason.trim() }),
            })
                .then(r => r.json().then(body => ({ status: r.status, body })))
                .then(({ status, body }) => {
                    if (status >= 400) {
                        alert((body && (body.message || body.error)) || 'Override failed');
                        this.$set(this.approving, row.id, false);
                        return;
                    }
                    this.rows = this.rows.filter(r => r.id !== row.id);
                    if (this.expanded === row.id) this.expanded = null;
                    this.$emit('approved', { quoteId: row.quote.id, stage: 'sales', override: true });
                })
                .catch(err => {
                    alert('Override failed: ' + (err.message || 'network error'));
                    this.$set(this.approving, row.id, false);
                });
        },
        itemSize(item) {
            const o = item.options || {};
            const w = o.artwork_width, h = o.artwork_height, d = o.artwork_depth;
            if (!w && !h) return '';
            let s = `${w || '?'}mm × ${h || '?'}mm`;
            if (d) s += ` × ${d}mm deep`;
            if (o.artwork_type) s += ` (${o.artwork_type})`;
            return s;
        },
        formatRelative(iso) {
            if (!iso) return '';
            const then = new Date(iso).getTime();
            const now = Date.now();
            const diffMins = Math.floor((now - then) / 60000);
            if (diffMins < 1) return 'just now';
            if (diffMins < 60) return `${diffMins}m ago`;
            const hours = Math.floor(diffMins / 60);
            if (hours < 24) return `${hours}h ago`;
            const days = Math.floor(hours / 24);
            return `${days}d ago`;
        },
    },
};
</script>
